import {useEffect, useRef} from 'react';
import {scrollerFor} from './useSectionObserver';

// La clase que marca un bloque como revelable, y la que lo deja revelado. El SCSS hace el resto:
// sin `-in` el bloque está transparente y 18px más abajo.
export const REVEAL_CLASS = 'iab-rv';
export const REVEALED_CLASS = '-in';

// Cuánto tiene que entrar un bloque antes de revelarse. Es el margen del mockup: un pelo antes del
// borde inferior, para que la animación arranque cuando el bloque ya se está viendo y no después.
const ROOT_MARGIN = '0px 0px -4% 0px';

/**
 * La aparición suave de los bloques al entrar en pantalla (mockup: `.rv` → `.rv.in`).
 *
 * Es de las cosas que más hacen que el Recorrido se sienta terminado: los umbrales y los bloques de
 * la banda no están ahí desde siempre, aparecen cuando el usuario llega. Sin esto la página es
 * correcta pero plana.
 *
 * Tres cosas que este hook resuelve y el mockup no necesitaba:
 *
 * 1. **Los bloques aparecen DESPUÉS del primer render.** La one-page monta sus paneles diferidos al
 *    acercarse al viewport (`LazySection`), así que un escaneo único al montar —lo que hace el
 *    mockup— dejaría sin revelar todo lo que llegue más tarde: el bloque nace transparente y se
 *    queda invisible **para siempre**. Por eso el escaneo corre en cada commit y el observer se crea
 *    una sola vez. Observar dos veces el mismo nodo es no-op, así que re-escanear no acumula nada.
 * 2. **El scroller no es la ventana.** Es `.app-content` (ver `useSectionObserver`): con `root: null`
 *    el observer mide contra el viewport y revela todo de una en cuanto la página es más alta que
 *    la pantalla.
 * 3. **`prefers-reduced-motion` revela TODO de entrada**, sin observer. El bloque tiene que estar
 *    visible igual: lo que se apaga es el movimiento, nunca el contenido.
 *
 * @param {object} containerRef  ref al contenedor donde buscar los bloques.
 * @param {boolean} enabled      si el Recorrido está dibujado de verdad.
 */
export function useRevealOnScroll(containerRef, enabled = true) {
    const observerRef = useRef(null);

    // El observer se crea una vez por contenedor. `enabled` lo apaga: antes de que los módulos
    // carguen no hay nada que revelar.
    useEffect(() => {
        const node = containerRef.current;
        if (!enabled || !node) return undefined;

        const reduced = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

        if (reduced || typeof window.IntersectionObserver !== 'function') {
            // Sin animación (o sin soporte), todo se revela y no se observa nada. El `querySelectorAll`
            // corre igual en el efecto de abajo, que también aplica esta rama.
            return undefined;
        }

        const observer = new window.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add(REVEALED_CLASS);
                // Una vez revelado no vuelve a esconderse: un bloque que se desvanece al salir de
                // pantalla y reaparece al volver marea, y no es lo que hace el mockup.
                observer.unobserve(entry.target);
            });
        }, {root: scrollerFor(node) || null, rootMargin: ROOT_MARGIN, threshold: 0.01});

        observerRef.current = observer;
        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, [containerRef, enabled]);

    // Sin array de dependencias A PROPÓSITO: corre después de CADA commit, que es lo que engancha
    // los bloques que montaron recién (los paneles diferidos, la biblioteca de prompts que llega
    // por fetch). Es un `querySelectorAll` sobre unas decenas de nodos y sólo mira los que todavía
    // no se revelaron.
    useEffect(() => {
        const node = containerRef.current;
        if (!enabled || !node) return;
        const pending = node.querySelectorAll(`.${REVEAL_CLASS}:not(.${REVEALED_CLASS})`);
        const observer = observerRef.current;
        pending.forEach((el) => {
            if (observer) observer.observe(el);
            else el.classList.add(REVEALED_CLASS);
        });
    });
}

export default useRevealOnScroll;
